// src/components/VotePage.js
import React, {useState, useEffect} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {motion} from 'framer-motion';
import {useElectionWebSocket} from '../hooks/useElectionWebSocket';
import {electionApi} from '../api/election';

// Имя кандидата: с 006-й миграции имена живут в member
const candName = (c) => `${c?.member?.first_name ?? ''} ${c?.member?.last_name ?? ''}`.trim();
const candPhoto = (c) => c?.photo_data || c?.photo_url || null;

const VotePage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    const preview = searchParams.get('preview') === 'true';

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedCandidateId, setSelectedCandidateId] = useState(null);
    const [voteSubmitted, setVoteSubmitted] = useState(false);
    const [spoiledPhrase, setSpoiledPhrase] = useState('');
    const [showSpoilModal, setShowSpoilModal] = useState(false);
    const [timeLeft, setTimeLeft] = useState(null);
    const [results, setResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [winner, setWinner] = useState(null);

    const {lastMessage} = useElectionWebSocket(token);

    const loadStatus = async () => {
        try {
            let res;
            if (preview) {
                res = await electionApi.previewElection();
            } else {
                res = await electionApi.getElectionStatus(token);
            }
            setStatus(res.data);
            if (res.data.effective_deadline) {
                setTimeLeft(new Date(res.data.effective_deadline) - new Date());
            }
            if (res.data.vote) {
                setSelectedCandidateId(res.data.vote.candidate_id);
                setVoteSubmitted(res.data.vote.spoiled ? true : true);
            }
            if (res.data.winner) {
                loadWinner();
            }
            // Наличие выбранного победителя
            if (res.data.winner_id) setWinner(null);
        } catch (err) {
            console.error('Ошибка загрузки статуса', err);
        } finally {
            setLoading(false);
        }
    };

    const loadWinner = async () => {
        try {
            const r = await electionApi.getWinner();
            setWinner(r.data);
        } catch (e) { /* не выбрали ещё */
        }
    };

    const loadRoundResults = async (roundNumber) => {
        try {
            const r = await electionApi.getRoundResults(roundNumber);
            setResults(r.data);
        } catch (e) {
            console.error('Не удалось загрузить результаты', e);
        }
    };

    useEffect(() => {
        if (!token && !preview) return;
        loadStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, preview]);

    // Обработка WebSocket сообщений
    useEffect(() => {
        if (!lastMessage) return;
        switch (lastMessage.type) {
            case 'init':
                setStatus(prev => ({
                    ...prev,
                    is_open: lastMessage.is_open,
                    round: lastMessage.round,
                    candidates: lastMessage.candidates,
                    effective_deadline: lastMessage.effective_deadline,
                }));
                if (lastMessage.effective_deadline) {
                    setTimeLeft(new Date(lastMessage.effective_deadline) - new Date());
                }
                break;
            case 'election_open_changed':
                setStatus(prev => ({...prev, is_open: lastMessage.is_open}));
                break;
            case 'round_started':
                setStatus(prev => ({...prev, round: lastMessage.round, candidates: lastMessage.candidates}));
                setTimeLeft(new Date(lastMessage.end_time) - new Date());
                setResults([]);
                setShowResults(false);
                setVoteSubmitted(false);
                setSelectedCandidateId(null);
                setSpoiledPhrase('');
                break;
            case 'round_finished':
                setStatus(prev => ({...prev, round: {...prev.round, status: 'finished'}}));
                setTimeLeft(0);
                break;
            case 'results_published':
                setStatus(prev => ({...prev, round: {...prev.round, status: 'results_published'}}));
                loadStatus();
                break;
            case 'winner_selected':
                setWinner(lastMessage.candidate);
                break;
            case 'election_reset':
                setStatus(prev => ({...prev, is_open: false, round: null, candidates: []}));
                setTimeLeft(null);
                setResults([]);
                setShowResults(false);
                setWinner(null);
                setSelectedCandidateId(null);
                setVoteSubmitted(false);
                setSpoiledPhrase('');
                break;
            default:
                break;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastMessage]);

    // Таймер обратного отсчёта
    useEffect(() => {
        if (!timeLeft || timeLeft <= 0) return;
        const interval = setInterval(() => {
            setTimeLeft(prev => {
                const newVal = prev - 1000;
                if (newVal <= 0) {
                    clearInterval(interval);
                    setStatus(s => ({...s, round: {...s.round, status: 'finished'}}));
                    return 0;
                }
                return newVal;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [timeLeft]);

    const formatTime = (ms) => {
        if (ms == null) return '00:00';
        if (ms <= 0) return '00:00';
        const totalSec = Math.floor(ms / 1000);
        const mins = Math.floor(totalSec / 60);
        const secs = totalSec % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    const handleVote = async (candidateId) => {
        if (!status?.round || status.round.status !== 'active') return;
        if (preview) {
            alert('Это предпросмотр. Голосование недоступно.');
            return;
        }
        try {
            await electionApi.vote(token, candidateId, false);
            setSelectedCandidateId(candidateId);
            setVoteSubmitted(true);
            setSpoiledPhrase('');
            setShowSpoilModal(false);
        } catch (err) {
            alert(err.response?.data?.detail || 'Ошибка голосования');
        }
    };

    const handleSpoil = async () => {
        if (!status?.round || status.round.status !== 'active') return;
        if (preview) {
            alert('Это предпросмотр. Голосование недоступно.');
            return;
        }
        const phrases = status.spoiled_phrases || [];
        const random = phrases.length ? phrases[Math.floor(Math.random() * phrases.length)] : 'Вот пакостник!';
        setSpoiledPhrase(random);
        setShowSpoilModal(true);
    };

    const handleViewResults = () => {
        loadRoundResults(status?.round?.round_number || 1);
        setShowResults(true);
    };

    const handleGoWinner = () => {
        navigate('/winner');
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center">Загрузка...</div>;

    if (!token && !preview) {
        return <div className="min-h-screen flex items-center justify-center">Не указан токен</div>;
    }

    const round = status?.round;
    const isActive = round && round.status === 'active';
    const isFinished = round && round.status === 'finished';
    const isResultsPublished = round && round.status === 'results_published';
    const candidates = status?.candidates || [];
    // Финал: 2-й тур, либо 1-й тур с опубликованными результатами (уже показываем финалистов)
    const inFinal = round && (round.round_number === 2 || round.status === 'results_published');

    // Собираем кнопки действий в зависимости от состояния
    const renderActionButton = () => {
        if (!round) return null;
        // После результатов 2 тура / наличия победителя
        if (winner) {
            return (
                <button onClick={handleGoWinner}
                        className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                    Новый следующий супружник
                </button>
            );
        }
        if (round.round_number === 2 && isResultsPublished) {
            // Победитель ещё не выбран админом — ждём
            return (
                <button disabled className="px-6 py-2 bg-gray-300 text-white rounded cursor-not-allowed">
                    Ожидание объявления победителя…
                </button>
            );
        }
        if (round.round_number === 2 && isFinished) {
            return (
                <button disabled className="px-6 py-2 bg-gray-300 text-white rounded cursor-not-allowed">
                    Новый следующий супружник
                </button>
            );
        }
        return null;
    };

    if (!preview && !status?.is_open && !round) {
        return (
            <div className="min-h-screen bg-gray-50 py-8 px-4">
                <div className="max-w-6xl mx-auto">
                    <h1 className="text-4xl font-display text-center mb-8">Выборы следующего супружника</h1>
                    <div className="text-center py-12">
                        <p className="text-xl">Голосование закрыто</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4">
            <div className="max-w-6xl mx-auto">
                {winner && (
                    <div className="flex justify-center gap-3 mb-6 flex-wrap">
                        {winner && (
                            <button onClick={handleGoWinner}
                                    className="px-8 py-3 bg-green-600 text-white rounded-full text-lg font-semibold hover:bg-green-700 shadow-lg">
                                🏆 Новый следующий супружник
                            </button>
                        )}
                    </div>
                )}

                <h1 className="text-4xl font-display text-center mb-8">
                    {inFinal ? 'Финал: выборы следующего супружника' : 'Выборы следующего супружника'}
                </h1>

                {preview && (
                    <div
                        className="bg-yellow-100 border border-yellow-300 text-yellow-800 p-3 rounded mb-4 text-center">
                        Режим предпросмотра – голосование недоступно
                    </div>
                )}

                {/* Таймер / статус тура */}
                {(isActive || isFinished) && (
                    <div className="text-center mb-8">
                        <div className="text-5xl font-bold tabular-nums">{formatTime(timeLeft)}</div>
                        {isActive
                            ? <p className="text-sm text-gray-500 mt-1">Осталось времени</p>
                            : <p className="text-sm text-gray-500 mt-1">Время голосования истекло</p>}
                    </div>
                )}

                {/* Кнопка "Испортить бюллетень" — на самом видном месте */}
                {isActive && (
                    <div className="flex justify-center mb-8">
                        <button onClick={handleSpoil} disabled={preview}
                                className="px-8 py-3 bg-red-500 text-white rounded-full text-lg font-semibold hover:bg-red-600 shadow-lg disabled:opacity-50">
                            🍂 Испортить бюллетень
                        </button>
                    </div>
                )}

                {/* Карточки */}
                <div
                    className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-4">
                    {candidates.map(c => (
                        <motion.div key={c.id} whileHover={{scale: isActive ? 1.02 : 1}}
                                    className={`bg-white rounded-lg shadow overflow-hidden border-2 ${selectedCandidateId === c.id && isActive ? 'border-green-500' : 'border-transparent'}`}>
                            <div className="aspect-[3/4] bg-gray-100 overflow-hidden">
                                {candPhoto(c) ? (
                                    <img src={candPhoto(c)} alt={candName(c)} className="w-full h-full object-cover"/>
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                        Нет фото
                                    </div>
                                )}
                            </div>
                            <div className="p-4">
                                <h3 className="text-xl font-bold">{candName(c)}</h3>
                                <p className="text-gray-600 text-sm mt-1 min-h-[3rem]">{c.description}</p>
                                {isActive && (
                                    <button onClick={() => handleVote(c.id)} disabled={preview}
                                            className={`mt-3 w-full py-2 rounded text-white transition ${selectedCandidateId === c.id ? 'bg-green-600' : 'bg-gold-accent hover:bg-yellow-600'} disabled:opacity-50`}>
                                        {selectedCandidateId === c.id ? '✓ Проголосовано' : 'Голосовать'}
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    ))}
                    {candidates.length === 0 && (
                        <div className="col-span-full text-center py-12 text-gray-500">
                            Кандидатов пока нет.
                        </div>
                    )}
                </div>

                {/* Блок после завершения тура */}
                {!isActive && round && (
                    <div className="text-center mt-10">
                        {renderActionButton()}
                    </div>
                )}

                {/* Ожидание начала */}
                {!round && (
                    <div className="text-center py-12 text-gray-500">
                        Голосование ещё не началось
                    </div>
                )}
            </div>

            {/* Модалка испорченного бюллетеня */}
            {showSpoilModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 text-center">
                        <div className="text-5xl mb-3">🍂</div>
                        <p className="text-xl text-red-600 font-semibold mb-4">Бюллетень сегодня не может быть
                            испорчен</p>
                        <p className="text-gray-700 italic mb-6">{spoiledPhrase}</p>
                        <p className="text-sm text-gray-400 mb-4">А ну быстро выбирать супружника!</p>
                        <div className="flex justify-center gap-3">
                            <button onClick={() => setShowSpoilModal(false)}
                                    className="px-6 py-2 bg-gray-200 rounded">Понятно
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showResults && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
                        <h2 className="text-2xl font-display mb-4">
                            {round?.round_number === 2 ? 'Итоги 2 тура' : 'Результаты 1 тура'}
                        </h2>
                        <ul className="space-y-2">
                            {results.map((item, idx) => (
                                <li key={item.candidate.id}
                                    className="flex justify-between items-center border-b py-2">
                                    <span className="flex items-center gap-3">
                                        <span className="text-gray-400 w-6">{idx + 1}.</span>
                                        {item.candidate.photo_data && (
                                            <img src={item.candidate.photo_data}
                                                 className="h-10 w-10 rounded object-cover" alt=""/>
                                        )}
                                        <span className="font-medium">{candName(item.candidate)}</span>
                                    </span>
                                    <span>{item.votes} голосов</span>
                                </li>
                            ))}
                        </ul>
                        <button onClick={() => setShowResults(false)}
                                className="mt-4 px-4 py-2 bg-gray-200 rounded">Закрыть
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VotePage;